import React, { useContext, useEffect } from "react";

import { ThemeProvider } from "styled-components";

import AuthContext from "shared/providers/context/AuthContext";
import GlobalContext from "shared/providers/context/GlobalContext";
import { TClassName, usePixel } from "shared/utils/hooks/Pixel";
import { useFeature } from "shared/utils/hooks/useFeature";
import useWindowSize from "shared/utils/hooks/useWindowSize";

import IOffer from "@CheckoutStepsOffers/@types/offer";
import { ICoupon } from "@CheckoutStepsOffers/provider";
import { getThemes } from "@CheckoutStepsOffers/themes";
import { checkContrast } from "@CheckoutStepsOffers/utils/checkContrast";
import { IInitialData } from "@payment/providers/CheckoutProvider/dtos";

import CongratsPurchase from "./components/organisms/CongratsPurchase";
import Countdown from "./components/organisms/Countdown";
import ProductInfo from "./components/organisms/ProductInfo";
import PurchaseForm from "./components/organisms/PurchaseForm";
import CheckoutOnePageProvider from "./provider";
import { useCheckoutOnepageOffersContext } from "./provider/useCheckoutOnepageOffersContext";
import {
  BannerCheckout,
  BannerCheckoutBottom,
  Container,
  LeftSide,
  RightSide,
  WrapperContainer,
} from "./styles";

const CheckoutOnePageOffersWithoutProvider = () => {
  const { checkoutOnePageState, offer } = useCheckoutOnepageOffersContext();
  const checkoutImageEnable = useFeature("f_checkout_images");
  const windowSize = useWindowSize();
  const isMobile = windowSize.width <= 690;

  const containers = [
    <PurchaseForm key="purchase-form" />,
    <CongratsPurchase key="congrats-purchase" />,
  ];

  return (
    <WrapperContainer>
      <Countdown />
      {checkoutImageEnable &&
        (isMobile ? (
          <BannerCheckout src={offer.offer.details.banner_top_mobile} />
        ) : (
          <BannerCheckout src={offer.offer.details.banner_top_desktop} />
        ))}

      <Container>
        <LeftSide>{containers[checkoutOnePageState.currentStep]}</LeftSide>

        <RightSide>
          <ProductInfo />
        </RightSide>
      </Container>
      {checkoutImageEnable &&
        (isMobile ? (
          <BannerCheckoutBottom
            src={offer.offer.details.banner_bottom_mobile}
          />
        ) : (
          <BannerCheckoutBottom
            src={offer.offer.details.banner_bottom_desktop}
          />
        ))}
    </WrapperContainer>
  );
};

interface ICheckoutOnepageOffers {
  offer: IOffer;
  promoterLink: string;
  initialData: IInitialData;
  couponInitialState: ICoupon;
  clientIp: string;
}

const CheckoutOnepageOffers = ({
  offer,
  initialData,
  promoterLink,
  couponInitialState,
  clientIp,
}: ICheckoutOnepageOffers) => {
  const { setTrackInitiateCheckout } = usePixel();

  const { loggedUser } = useContext(AuthContext);
  const { state } = useContext(GlobalContext);
  const selectedTheme = getThemes(state.theme.value);

  const checkoutPrimaryColor = offer?.offer?.details?.color;
  const checkoutTextPrimaryColor =
    checkContrast(checkoutPrimaryColor) === "black" ? "#262626" : "#F2F2F2";

  const theme = {
    checkoutStepsOffers: {
      ...selectedTheme,
      checkoutPrimaryColor,
      checkoutTextPrimary: checkoutTextPrimaryColor,
    },
  };

  useEffect(() => {
    const payload = {
      pixelResponse: null,
      currency: "BRL",
      productId: offer.group.id,
      slug: offer.group.slug,
      value: Number(offer.offer.price),
      className: "App\\Group" as TClassName,
      productName: offer.group.title,
      offer_name: offer.offer.details.title,
      promoterLink: promoterLink,
    };

    setTrackInitiateCheckout(payload, loggedUser);
  }, []);

  return (
    <ThemeProvider theme={theme as any}>
      <CheckoutOnePageProvider
        offer={offer}
        initialData={initialData}
        promoterLink={promoterLink}
        couponInitialState={couponInitialState}
        clientIp={clientIp}
      >
        <CheckoutOnePageOffersWithoutProvider />
      </CheckoutOnePageProvider>
    </ThemeProvider>
  );
};

export default CheckoutOnepageOffers;
